import React, { useState, useEffect } from "react";
import { useLocation, Redirect } from "react-router-dom";
import { auth } from "../firebase";
import useAuth from "../hooks/useAuth";
import { FiLock } from "react-icons/fi";
import "../styles/quotation-system.css";
import InstallAppButton from "./InstallAppButton";
import { useInstallPrompt } from "../context/InstallPromptContext";

export default function Login() {
  const location = useLocation();
  const { user, profile, loading: authLoading } = useAuth();
  const { isStandalone } = useInstallPrompt();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const metaTag = document.createElement("meta");
    metaTag.name = "robots";
    metaTag.content = "noindex";
    document.head.appendChild(metaTag);
    document.body.classList.add('login-page');
    return () => {
      document.head.removeChild(metaTag);
      document.body.classList.remove('login-page');
    };
  }, []);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="quotation-system" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div className="loading-elegant"></div>
      </div>
    );
  }

  // If already authenticated, redirect immediately (let routing handle it)
  if (user && profile) {
    if (profile.role === "customer") {
      return <Redirect to="/tracking" />;
    }
    if (profile.role === "contentwriter") {
      return <Redirect to="/writer/blogs" />;
    }
    const from = location.state?.from || (profile.role === "admin" ? "/admin" : "/q");
    return <Redirect to={from} />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await auth.signInWithEmailAndPassword(form.email.trim(), form.password);
      // Don't redirect here - let the redirect above handle it after auth state updates
      // The onAuthStateChanged will update user/profile, triggering the redirect
    } catch (err) {
      setError(err.message || "Login failed");
      setLoading(false);
    }
  };

  return (
    <div className="quotation-system" style={{ 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center", 
      minHeight: "100vh", 
      padding: "1rem",
      width: "100%",
      boxSizing: "border-box"
    }}>
      <div className="elegant-card fade-in-up" style={{ 
        maxWidth: "440px", 
        width: "100%", 
        margin: "0 auto",
        boxSizing: "border-box"
      }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ 
            width: "64px", 
            height: "64px", 
            margin: "0 auto 1.5rem", 
            background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #06b6d4 100%)", 
            borderRadius: "16px", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            boxShadow: "0 8px 24px rgba(139, 92, 246, 0.4), 0 4px 12px rgba(236, 72, 153, 0.3)",
            position: "relative",
            overflow: "hidden"
          }}>
            <FiLock style={{ fontSize: "2rem", zIndex: 1, position: "relative", color: "white" }} />
            <div style={{
              position: "absolute",
              top: "-50%",
              left: "-50%",
              width: "200%",
              height: "200%",
              background: "radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)",
              animation: "spin 3s linear infinite"
            }}></div>
          </div>
          <h1 className="elegant-title" style={{ fontSize: "2rem", marginBottom: "0.5rem", color: "var(--primary-deep)" }}>Welcome Back</h1>
          <p className="elegant-subtitle" style={{ fontSize: "0.9375rem", marginBottom: 0 }}>Sign in to access your dashboard</p>
        </div>
        
        <form onSubmit={onSubmit}>
          <div className="form-group-elegant">
            <label className="form-label-elegant">Email Address</label>
            <input
              type="email"
              className="form-input-elegant"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="your.email@example.com"
              required
            />
          </div>
          
          <div className="form-group-elegant">
            <label className="form-label-elegant">Password</label>
            <input
              type="password"
              className="form-input-elegant"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Enter your password"
              required
            />
          </div>
          
          {error && (
            <div style={{ 
              padding: "0.875rem 1rem", 
              marginBottom: "1.5rem", 
              background: "rgba(239, 68, 68, 0.1)", 
              border: "1px solid rgba(239, 68, 68, 0.3)", 
              borderRadius: "8px", 
              color: "#ef4444",
              fontSize: "0.875rem",
              fontWeight: "500"
            }}>
              {error}
            </div>
          )}
          
          <button 
            type="submit" 
            className="btn-elegant btn-elegant-primary" 
            disabled={loading}
            style={{ width: "100%", marginTop: "0.5rem" }}
          >
            {loading ? (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
                <span className="loading-elegant"></span>
                Signing in...
              </span>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {!isStandalone && (
          <div style={{ marginTop: "1.75rem" }}>
            <p className="elegant-subtitle" style={{ marginBottom: "0.75rem" }}>
              Install the Lunar Quotes web app for one-tap access in the field.
            </p>
            <InstallAppButton variant="block" />
          </div>
        )}
      </div>
    </div>
  );
}


